"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { ArrowRight, Loader, Mail } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const CONTACT_INFO = {
  email: "bryan@thebarndesk.com"
};
const SOCIAL_LINKS = [{
  name: "Instagram",
  url: "https://www.instagram.com/barndesk/"
}, {
  name: "Facebook",
  url: "https://www.facebook.com/profile.php?id=61589368375810"
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-2xl border border-border bg-card p-8 text-center shadow-md">
        <div className="flex size-12 items-center justify-center rounded-lg bg-accent text-accent-foreground">
          <Mail className="size-6" />
        </div>
        <div className="space-y-2">
          <h3 className="font-default text-xl font-bold text-primary">Message Received</h3>
          <p className="text-muted-foreground font-default">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Send Another Message
        </Button>
      </div>;
  }
  const inputClasses = "flex h-12 w-full rounded-lg border-2 border-accent bg-card px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all";
  const textareaClasses = "flex min-h-[140px] w-full rounded-lg border-2 border-accent bg-card px-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all resize-y";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold text-primary">Your Name</FormLabel>
            <FormControl>
              <input type="text" placeholder="Your name" className={inputClasses} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/70">How should we address you?</FormDescription>
            <FormMessage />
          </FormItem>} />
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-primary">Email Address</FormLabel>
              <FormControl>
                <input type="email" placeholder="you@yourfarm.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Invalid phone format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-primary">Phone Number</FormLabel>
              <FormControl>
                <input type="tel" placeholder="(555) 000-0000" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/70">Optional — only if you'd rather we call.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>
      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold text-primary">Message</FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about your farm or ask a question..." className={textareaClasses} {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/70">
              Questions about the app, your records, or anything else.
            </FormDescription>
            <FormMessage />
          </FormItem>} />
      {submitError && <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}
      <div className="flex flex-col gap-4">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition-all shadow-md" size="lg">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Sending...
            </> : <>
              Send a Message
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
        <div className="inline-flex items-center px-3 py-1 rounded-lg bg-accent/50 text-accent-foreground text-xs font-semibold w-fit">
          Usually a reply within a day or two — we're a working farm.
        </div>
      </div>
    </Form>;
}
export default function GetInTouch() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="222878" id="need-help-reach-out" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden relative" ref={ref}>
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] rounded-lg bg-accent/30 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[30%] h-[30%] rounded-lg bg-primary/10 blur-[100px]" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-8">
          <div className={`lg:col-span-4 flex flex-col justify-between transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div>
              <h2 className="font-serif text-4xl font-bold tracking-tight text-primary sm:text-5xl mb-6">
                Farmer-to-Farmer Support
              </h2>
              <p className="text-muted-foreground font-default text-lg mb-8 max-w-md leading-relaxed">
                Have a question about tracking animals, scanning receipts, or just want
                to talk shop? Send a note — a real farmer will read it.
              </p>

              {/* Optional: drop a real farm photo here when you have one ready.
                  For now, the section feels cleaner without the stock image. */}

              <div className="space-y-4 font-default">
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-bold">
                    Email
                  </h3>
                  <Link to={`mailto:${CONTACT_INFO.email}`} className="inline-flex items-center text-foreground font-semibold hover:text-primary transition-colors text-base">
                    <Mail className="mr-2 size-4 text-primary" />
                    {CONTACT_INFO.email}
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-16 pt-8 border-t border-border">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-4 font-bold">
                Social
              </h3>
              <div className="flex gap-6 font-default text-sm">
                {SOCIAL_LINKS.map((link, i) => <div key={link.name} data-index={i}>
                    <Link to={link.url} className="text-foreground font-medium hover:text-primary transition-colors relative after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all hover:after:w-full">
                      {link.name}
                    </Link>
                  </div>)}
              </div>
            </div>
          </div>

          <div className={`lg:col-span-7 lg:col-start-6 transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="rounded-3xl border border-border bg-card/80 backdrop-blur-md p-8 sm:p-10 shadow-xl shadow-shadow-color/10">
              <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="Need Help? Reach Out" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              message: {
                required: true
              }
            }} submitText="Send a Message" successMessage="Thanks for reaching out! We'll get back to you as soon as we're in from the field.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}